import { Box } from "@mui/material";
import * as React from "react";
import { StepTitle } from "./step-title.tsx";
import { Step, StepperNavigationProps, Substep } from "./types.ts";

export function StepperNavigation({ steps, setSteps, onStepChange, activeStep, multiExpand = false, size = 'md', canNavigateToStep, highlightIncomplete = false }: StepperNavigationProps) {
  // Update expanded steps based on activeStep prop
  React.useEffect(() => {
    if (activeStep) {
      // Check if activeStep is a substep by looking for it in all steps' substeps
      const parentStep = steps.find((step) => step.substeps?.some((substep) => substep.id === activeStep));

      if (parentStep && !parentStep.expanded) {
        // If activeStep is a substep, expand its parent
        setSteps((prevSteps) =>
          prevSteps.map((step) => {
            if (step.id === parentStep.id) {
              return { ...step, expanded: true };
            }
            // If multiExpand is false, collapse other steps
            if (!multiExpand && step.expanded && step.id !== parentStep.id) {
              return { ...step, expanded: false };
            }
            return step;
          })
        );
      }
    }
  }, [activeStep, steps, multiExpand, setSteps]);

  const handleToggleExpand = (stepId: string) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => {
        if (step.id === stepId) {
          return { ...step, expanded: !step.expanded };
        }
        // If multiExpand is false, collapse other steps when expanding one
        if (!multiExpand && step.expanded && step.id !== stepId) {
          return { ...step, expanded: false };
        }
        return step;
      })
    );
  };

  const handleStepClick = (step: Step) => {
    // Check if navigation is allowed
    let targetStepId = step.id;
    if (step.substeps && step.substeps.length > 0) {
      targetStepId = step.substeps[0].id;
    }
    
    if (canNavigateToStep && !canNavigateToStep(targetStepId)) {
      console.log("Navigation blocked for step:", targetStepId);
      return;
    }
    
    // If the step is expandable, toggle its expansion when clicked
    if (step.expandable) {
      handleToggleExpand(step.id);
    }
    onStepChange?.(step);
  };

  const handleSubstepClick = (substep: Substep) => {
    // Check if navigation is allowed
    if (canNavigateToStep && !canNavigateToStep(substep.id)) {
      console.log("Navigation blocked for substep:", substep.id);
      return;
    }
    
    // Add parentStepId to substep when clicking
    const parentStep = steps.find((step) => step.substeps?.some((s) => s.id === substep.id));

    const substepWithParent: Substep = {
      ...substep,
      parentStepId: parentStep?.id,
    };

    onStepChange?.(substepWithParent);
  };

  // Create steps with updated active states based on activeStep prop
  const stepsWithActiveStates = steps.map((step) => {
    const hasActiveSubstep = step.substeps?.some((substep) => substep.id === activeStep);
    const isStepActive = activeStep ? activeStep === step.id || hasActiveSubstep : false;

    // Determine if step is navigable
    let stepNavigable = true;
    if (canNavigateToStep) {
      if (step.substeps && step.substeps.length > 0) {
        stepNavigable = canNavigateToStep(step.substeps[0].id);
      } else {
        stepNavigable = canNavigateToStep(step.id);
      }
    }

    return {
      ...step,
      active: isStepActive,
      navigable: stepNavigable,
      substeps: step.substeps?.map((substep) => ({
        ...substep,
        active: activeStep ? activeStep === substep.id : substep.active,
        parentStepId: step.id,
        navigable: canNavigateToStep ? canNavigateToStep(substep.id) : true,
      })),
    };
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {stepsWithActiveStates.map((step, index) => (
        <StepTitle
          key={step.id}
          step={step}
          title={step.title}
          expandable={step.expandable}
          expanded={step.expanded}
          complete={step.complete}
          active={step.active}
          isLast={step.isLast || index === steps.length - 1}
          substeps={step.substeps}
          onToggleExpand={() => handleToggleExpand(step.id)}
          onStepClick={handleStepClick}
          onSubstepClick={handleSubstepClick}
          stepperSize={size}
          navigable={step.navigable}
          highlightIncomplete={highlightIncomplete}
          neutralDisplay={step.neutralDisplay}
        />
      ))}
    </Box>
  );
}